import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSuppliers, getSupplierTransactions, addSupplierTransaction } from '../services/api';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './SupplierDetailsPage.css';

function SupplierDetailsPage() {
  const { supplierId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setDynamicLabel } = useBreadcrumb();
  const [supplier, setSupplier] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('payable'); // 'payable' or 'history'

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [transactionType, setTransactionType] = useState('INVOICE'); // INVOICE or PAYMENT
  const [selectedInvoice, setSelectedInvoice] = useState(null); // For paying a specific invoice

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
  });

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  useEffect(() => {
    if (supplier) setDynamicLabel(supplier.name);
  }, [supplier, setDynamicLabel]);

  const loadData = async () => {
    try {
      const suppliersData = await getSuppliers();
      const foundSupplier = suppliersData.suppliers.find((s) => s.supplierId === supplierId);
      setSupplier(foundSupplier);

      const transactionsData = await getSupplierTransactions(supplierId);
      setTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error('Error loading supplier data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type, invoice = null) => {
    setTransactionType(type);
    setSelectedInvoice(invoice);

    // If paying an invoice, pre-fill amount with remaining debt
    let initialAmount = '';
    let description = '';

    if (type === 'PAYMENT' && invoice) {
      const remaining = invoice.totalAmount - (invoice.paidAmount || 0);
      initialAmount = remaining.toFixed(2);
      description = `Pago Factura: ${invoice.description || invoice.date}`;
    }

    setFormData({
      amount: initialAmount,
      description: description,
      date: new Date().toISOString().split('T')[0],
      dueDate: '', // only for invoices
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        description: formData.description,
        date: formData.date,
        type: transactionType,
      };

      if (transactionType === 'INVOICE') {
        payload.dueDate = formData.dueDate || undefined;
      } else if (transactionType === 'PAYMENT' && selectedInvoice) {
        payload.relatedTransactionId = selectedInvoice.transactionId;
      }

      await addSupplierTransaction(supplierId, payload);
      setModalOpen(false);
      loadData();
      showFeedback('success', t('suppliers.transactionSuccess'));
    } catch (error) {
      console.error('Error adding transaction:', error);
      showFeedback('error', t('suppliers.transactionError'));
    }
  };

  // Derived state
  const pendingInvoices = transactions
    .filter((tx) => tx.type === 'INVOICE' && (tx.status === 'PENDING' || tx.status === 'PARTIAL'))
    .sort((a, b) => new Date(a.dueDate || a.date) - new Date(b.dueDate || b.date));

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (!supplier)
    return <div className="error">{t('suppliers.notFound', 'Proveedor no encontrado')}</div>;

  return (
    <div className="supplier-details-page">
      <div className="page-header">
        <h1>{supplier.name}</h1>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="supplier-info-card">
        <div className="info-row">
          <label>{t('suppliers.balance', 'Saldo (Deuda Total)')}:</label>
          <span
            className={supplier.balance > 0 ? 'text-danger' : 'text-success'}
            style={{ fontSize: '1.5em', fontWeight: 'bold' }}
          >
            ${parseFloat(supplier.balance || 0).toFixed(2)}
          </span>
        </div>
        <div className="actions-bar">
          {/* General actions */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleOpenModal('INVOICE')}
          >
            {t('suppliers.registerInvoice', 'Registrar Nueva Factura')}
          </button>
          <button type="button" className="btn-success" onClick={() => handleOpenModal('PAYMENT')}>
            {t('suppliers.registerGeneralPayment', 'Registrar Pago General')}
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'payable' ? 'active' : ''}`}
          onClick={() => setActiveTab('payable')}
        >
          Cuentas por Pagar ({pendingInvoices.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Historial Completo
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'payable' && (
          <div className="table-responsive">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Vencimiento</th>
                  <th>Descripción</th>
                  <th>Total</th>
                  <th>Pagado</th>
                  <th>Saldo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvoices.length > 0 ? (
                  pendingInvoices.map((inv) => {
                    const total = inv.totalAmount || parseFloat(inv.amount);
                    const paid = inv.paidAmount || 0;
                    const remaining = total - paid;
                    const isExpired = inv.dueDate && new Date(inv.dueDate) < new Date();

                    return (
                      <tr key={inv.transactionId}>
                        <td>{new Date(inv.date).toLocaleDateString()}</td>
                        <td
                          style={{
                            color: isExpired ? 'red' : 'inherit',
                            fontWeight: isExpired ? 'bold' : 'normal',
                          }}
                        >
                          {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '-'}
                        </td>
                        <td>{inv.description}</td>
                        <td>${total.toFixed(2)}</td>
                        <td>${paid.toFixed(2)}</td>
                        <td>
                          <strong>${remaining.toFixed(2)}</strong>
                        </td>
                        <td>
                          <span
                            className={`badge badge-${inv.status === 'PARTIAL' ? 'info' : 'warning'}`}
                          >
                            {inv.status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-sm btn-success"
                            onClick={() => handleOpenModal('PAYMENT', inv)}
                          >
                            Pagar
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No hay facturas pendientes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="table-responsive">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>{t('common.date', 'Fecha')}</th>
                  <th>{t('common.type', 'Tipo')}</th>
                  <th>{t('common.description', 'Descripción')}</th>
                  <th>{t('common.amount', 'Monto')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((tx) => (
                    <tr key={tx.transactionId}>
                      <td>{new Date(tx.date).toLocaleDateString()}</td>
                      <td>
                        <span
                          className={`badge badge-${tx.type === 'INVOICE' ? 'warning' : 'success'}`}
                        >
                          {tx.type === 'INVOICE' ? 'Factura' : 'Pago'}
                        </span>
                      </td>
                      <td>{tx.description}</td>
                      <td className={tx.type === 'INVOICE' ? 'text-danger' : 'text-success'}>
                        {tx.type === 'INVOICE' ? '+' : '-'}${parseFloat(tx.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {transactionType === 'INVOICE'
                ? t('suppliers.newInvoice', 'Nueva Factura')
                : t('suppliers.newPayment', 'Nuevo Pago')}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('common.amount', 'Monto')}</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  step="0.01"
                  required
                />
              </div>

              {transactionType === 'INVOICE' && (
                <div className="form-group">
                  <label>Fecha de Vencimiento (Opcional)</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('common.description', 'Descripción')}</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={
                    transactionType === 'INVOICE' ? 'Nro Factura...' : 'Pago efectivo...'
                  }
                />
              </div>
              <div className="form-group">
                <label>{t('common.date', 'Fecha')}</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className={`btn-${transactionType === 'INVOICE' ? 'primary' : 'success'}`}
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierDetailsPage;
